/* Copyright (c) 2020-present CloudIO Inc */

import {
  isAllowedInsideParentDuplicateCheck,
  toOptions,
} from '../../utils/ManagerUtils';
import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './Menu';

const MenuManager: ComponentManagerFn<'Menu'> = (
  platform: Platform,
): ComponentManager<'Menu'> => {
  return {
    ...baseComponentManager<'Menu'>(platform, 'Menu'),
    type: 'Menu',
    name: 'Menu',
    isChildAllowed: (page, item, childType) =>
      (['MenuItem', 'MenuItemDivider', 'MenuItemList'] as ItemType[]).includes(
        childType,
      ),
    isAllowedInsideParent: (page, item, context) =>
      isAllowedInsideParentDuplicateCheck(
        platform,
        page,
        item,
        'Menu',
        context,
        'ALL',
      ),
    defaultValue,
    defaultChildren: ['MenuItem', 'MenuItem', 'MenuItemDivider', 'MenuItem'],
    props: {
      placement: {
        label: 'Placement',
        type: 'select',
        options: toOptions([
          'auto',
          'auto-end',
          'auto-start',
          'bottom',
          'bottom-end',
          'bottom-start',
          'left',
          'left-end',
          'left-start',
          'right',
          'right-end',
          'right-start',
          'top',
          'top-end',
          'top-start',
        ]),
        helpText: 'Specify the placement for the popover to display.',
        required: true,
      },
    },
    category: 'Toolbar',
    canHaveChildren: () => true,
    showEmptyPlaceholderInDesignerView: false,
    noOverlay: true,
    icon: { iconName: 'list-alt' },
    description: `**Overview**<br/>
The Menu component is a structural element that creates a temporary list of actionable choices (a dropdown or context menu). It is strictly a container that appears when triggered by a parent interaction (like clicking a button or avatar).
Use this component to group related actions, such as "File Options" (New, Open, Save) or "User Settings" (Profile, Account, Logout).

**1. Structure & Content**<br/>
The Menu is defined by the items it holds.
* **Allowed Children:** MenuItem (Individual clickable options), MenuItemDivider (Visual separators to group items), and MenuItemList (A way to likely populate menu items dynamically from a list).
* **Default Content:** Initializes with a sample set of items (Item, Item, Divider, Item) to help you visualize the structure immediately.

**2. Constraints**<br/>
* **Unique Parent:** The component includes a check (isAllowedInsideParentDuplicateCheck) that typically prevents adding more than one Menu to the same parent. A button usually triggers only one specific menu.

**3. Configuration**<br/>
* **Placement:** Controls exactly where the menu pop-up appears relative to its trigger element (e.g., bottom-start, right, top-end). This allows you to fine-tune the behavior for different layouts (e.g., a menu on the right side of the screen should probably open to the left or bottom-end to stay on screen).

**4. Best Use Cases**<br/>
* **Context Menus:** "More Options" (...) button on a table row.
* **Navigation:** A "Resources" dropdown in the main header.
* **Account Actions:** The menu that appears when clicking the user avatar.`,
  };
};

export default MenuManager;
